import { outBounce, linear } from "./easing";

const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
};

const starPath2D = 'M38.0386 78.1941C38.4673 80.3378 41.5322 80.3378 41.9609 78.1941L47.7383 49.3074C47.8966 48.5157 48.5155 47.8968 49.3072 47.7385L78.1939 41.9611C80.3376 41.5324 80.3376 38.4675 78.1939 38.0388L49.3072 32.2614C48.5155 32.1031 47.8966 31.4842 47.7383 30.6925L41.9609 1.80576C41.5322 -0.337929 38.4673 -0.33792 38.0386 1.80577L32.2612 30.6925C32.1029 31.4842 31.484 32.1031 30.6923 32.2614L1.80552 38.0388C-0.338172 38.4675 -0.338165 41.5324 1.80553 41.9611L30.6923 47.7385C31.484 47.8968 32.1029 48.5157 32.2612 49.3074L38.0386 78.1941Z'
const starOriginalSize = 80;

const generateStar = ({ maxWidth, maxHeight, index }) => {
    const size = Math.random() * Math.min(maxWidth, maxHeight) * 0.05 + 10;

    return {
        x: Math.random() * maxWidth,
        y: Math.random() * maxHeight,
        size,
        opacity: 1,
        scale: 1,
        duration: 400,
        delay: index * 20,
        targetOpacity: 0,
        targetSize: size * 3,
        done: false,
        show: false
    }
}

const generateStars = ({ maxWidth, maxHeight, length }) => {
    return Array.from({ length }, (_, i) => generateStar({ maxWidth, maxHeight, index: i + 1 }));
}

export class CanvasPixelated {
    constructor(canvas, { pixelSize = .2, image }) {
        this.canvas = canvas;
        this.pixelSize = pixelSize;
        this.targetPixelSize = pixelSize;
        this.ctx = canvas.getContext("2d");
        this.image = null;
        this.imageSrc = image;
        this.imageData = null;
        this.startTime = null;
        this.duration = 3000;
        this.stars = [];
        this.amountOfStars = 50;
        this.animating = false;
        this.minPixelSize = 4;
        this.isFirstRender = true;
        this._init();
    }

    async _init() {
        this.canvas.style.imageRendering = "pixelated";
        this.ctx.imageSmoothingEnabled = false;

        try {
            await this._setImage(this.imageSrc);
            this.pixelSize = Math.round(this.pixelSize * this.canvas.width);
            this._render();
        } catch (error) {
            console.error('Error loading image');
        }
    }

    async _setImage(src) {
        this.image = await loadImage(src);
        this.canvas.width = this.image.width;
        this.canvas.height = this.image.height;
        this.ctx.drawImage(this.image, 0, 0);
        this.imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    }

    _renderImage() {
        if (!this.image) return;

        const { width, height } = this.canvas;

        if (this.pixelSize <= this.minPixelSize) {
            this.ctx.drawImage(this.image, 0, 0, width, height);
            return;
        }

        for (let y = 0; y < height; y += this.pixelSize) {
            for (let x = 0; x <= width; x += this.pixelSize) {
                const pixelIndex = (y * width + x) * 4;
                const r = this.imageData.data[pixelIndex];
                const g = this.imageData.data[pixelIndex + 1];
                const b = this.imageData.data[pixelIndex + 2];
                const a = this.imageData.data[pixelIndex + 3];
                this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;

                this.ctx.fillRect(x, y, this.pixelSize, this.pixelSize);
            }
        }
    }

    _renderStar({ x = 0, y = 0, size = 100, opacity = 1 }) {
        // fill circle 
        const circleOpacity = 0.3 * opacity;
        this.ctx.fillStyle = `rgba(255, 255, 255, ${circleOpacity})`;
        this.ctx.beginPath();
        this.ctx.arc(x, y, size * .5, 0, Math.PI * 2);
        this.ctx.fill();
        // fill star
        const star = new Path2D(starPath2D);
        this.ctx.save();
        this.ctx.translate(x - (size * .5), y - (size * .5));
        this.ctx.scale(size / starOriginalSize, size / starOriginalSize);
        this.ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        this.ctx.fill(star);
        this.ctx.restore();
    }


    _update(timestamp) {
        // shared
        if (!this.startTime) {
            this.startTime = timestamp
        }
        const elapsedTime = timestamp - this.startTime;

        // image
        const progress = elapsedTime / this.duration;
        const easing = outBounce(progress);

        this.pixelSize = Math.round(this.pixelSize + (this.targetPixelSize - this.pixelSize) * easing);

        // stars
        this.stars.forEach((star) => {
            if (star.done) return;
            const starElapsedTime = elapsedTime - star.delay;
            if (starElapsedTime < 0) return;

            const progress = starElapsedTime / star.duration;
            const easing = linear(progress);
            star.opacity = star.opacity + (star.targetOpacity - star.opacity) * easing;
            star.size = star.size + (star.targetSize - star.size) * easing;
            star.show = true;

            if (progress >= 1) {
                star.done = true;
            }
        });
    }

    _checkIfDone() {
        const allStarsDone = this.stars.every(star => star.done);
        const hasImagePixelated = this.pixelSize === this.targetPixelSize;

        return allStarsDone && hasImagePixelated;
    }

    _render() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this._renderImage();

        if (this.isFirstRender) return

        this.stars
            .filter(star => star.show && !star.done)
            .forEach((star) => this._renderStar(star));
    }

    discover({ pixelSize }) {
        if (!this.image) {
            // wait for image to load
            setTimeout(() => this.discover({ pixelSize }), 100);
            return;
        }

        if (this.animating) return

        this.animating = true;
        this.startTime = null
        this.stars = generateStars({
            maxWidth: this.canvas.width,
            maxHeight: this.canvas.height,
            length: this.amountOfStars
        });
        this.targetPixelSize = Math.round(pixelSize * this.canvas.width);

        return new Promise((resolve) => {
            const loop = (timestamp) => {
                this._update(timestamp);
                this._render();

                if (this._checkIfDone()) {
                    resolve()
                    this.isFirstRender = false;
                    this.animating = false;
                } else {
                    requestAnimationFrame(loop)
                }
            }
            requestAnimationFrame(loop)
        })
    }
}